import { type ComponentProps } from 'react'
import { twMerge } from 'tailwind-merge'
import { hotkeyKeys } from '../../lib/hotkey'

interface KbdProps extends Omit<ComponentProps<'kbd'>, 'children'> {
  /** Keys joined by "+", e.g. "cmd+shift+k". Named keys become glyphs: ⌘, ⇧, ↩. */
  hotkey: string
}

/**
 * A hotkey as keycap chips, one per key. The chips read their size and colour from custom
 * properties so a host can fit them to itself without a variant per host: `--kbd-h` (chip height,
 * 18px), `--kbd-gap` (2px), `--kbd-bg` (the element wash) and `--kbd-ink` (secondary text). Type
 * size comes from the wrapper's `text-*`. Each chip's corner is half its height.
 */
function Kbd({ hotkey, className, ...props }: KbdProps) {
  return (
    <kbd
      data-slot="kbd"
      className={twMerge('inline-flex shrink-0 items-center gap-[var(--kbd-gap,2px)] font-sans text-xs', className)}
      {...props}
    >
      {hotkeyKeys(hotkey).map((key, index) => (
        <span
          key={`${key}-${index}`}
          className={twMerge(
            'corner-squircle inline-flex items-center justify-center px-1 font-medium tabular-nums',
            'bg-[var(--kbd-bg,var(--element))] text-[var(--kbd-ink,var(--foreground-secondary))]',
            'h-[var(--kbd-h,18px)] min-w-[var(--kbd-h,18px)] rounded-[calc(var(--kbd-h,18px)/2)]',
          )}
        >
          {key}
        </span>
      ))}
    </kbd>
  )
}

export { Kbd }
export type { KbdProps }
